import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { reduxStore } from 'reduxStore'
import { setAppError, setUserAuthenticated } from 'store/appSlice'

interface ApiError {
  err_code: number
  err_desc: string
}

const isApiError = (value: unknown): boolean => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'err_code' in value &&
    'err_desc' in value &&
    typeof (value as ApiError).err_code === 'number' &&
    typeof (value as ApiError).err_desc === 'string'
  )
}

const processError = (error: Error) => {
  const fatalError = {
    description: 'Something went wrong.',
    isFatal: true
  }

  if (!(error instanceof AxiosError)) {
    console.error('Fatal non-Axios error occurred:', error)
    reduxStore.dispatch(setAppError(fatalError))
    return
  }

  const eventId = error.response?.headers['x-event-id']
  if (!error.response || !isApiError(error.response.data)) {
    console.error('Fatal Axios non-API error occurred:', error, eventId)
    reduxStore.dispatch(setAppError({
      ...fatalError,
      ...(eventId && { event_id: eventId })
    }))
    return
  }

  const errorStatus = error.response.status
  const errorDescription = error.response.data.err_desc
  if (errorStatus === 401) {
    // User is not authenticated, so the Redux state is updated to trigger the ErrorBoundary which in turn will show the LoginPage
    reduxStore.dispatch(setUserAuthenticated(false))
    return
  }

  // Set the API error in the Redux state
  // This is a blanket catch-all for all API errors at the moment, but should be refined in the future
  const otherApiError = {
    description: errorDescription,
    isFatal: false,
    ...(eventId && { event_id: eventId })
  }
  reduxStore.dispatch(setAppError(otherApiError))
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      processError(error)
    }
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      processError(error)
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: false
    }
  }
})
